<template>
  <section class="space-y-3">
    <p
      class="text-text-neutral-tertiary flex items-center justify-between text-xs"
    >
      <span>{{ t('general.passengers') }}</span>
      <span>{{ validPassengersAmount }}</span>
    </p>
    <section class="space-y-4">
      <MultiplePassengerBlock
        v-for="(key, idx) in passengerKeys"
        :key="key"
        :pax-idx="idx"
        :schema="schema[key]"
        :passenger-key="key"
        :passenger="getTypedPassenger(key)"
        :saved-passengers="savedPassengers"
        :is-domestic="isDomestic"
        :provider-class="providerClass"
        :departure-date="departureDate"
        @onPassengerUpdate="emit('onPassengerUpdate', $event)"
        @onOpenSavedPassengersSheet="
          handleOpenSavedPassengersSheet(key, $event)
        "
      />
    </section>
    <PassengerAddMethodBottomSheet
      v-if="activePassengerKey"
      :name="passengerAddMethodBottomSheetName"
      :passenger-type="activePassengerType"
      @onManual="onAddMethodManual"
      @onPhoto="onAddMethodPhoto"
    />
    <PassengerDocumentPhotoBottomSheet
      :name="passengerDocumentPhotoBottomSheetName"
      :api-client="api.client"
      @onAutofillFlowComplete="handleDocumentAutofillComplete"
      @onEditRequest="handleDocumentAutofillEdit"
      @onPublicOfferClick="bottomSheet.show(docScanPublicOfferBsName)"
    />
    <PublicOfferBottomSheet
      :name="docScanPublicOfferBsName"
      :fetch-public-offer="publicOfferApi.fetchPublicOffer"
    />
    <SavedPassengersBottomSheet
      :name="savedPassengersBottomSheetName"
      :passengers="combinedPassengers"
      :passenger-on-card="activeSelectedPassenger"
      :passenger-type="activePassengerType"
      :is-loading="accountData.isLoading.value"
      :provider-class="providerClass"
      @onPassengerUpdate="handlePassengerUpdate"
      @onPassengerFormOpen="handlePassengerFormOpen"
      @onAddNewPassenger="handleAddNewPassengerRequest"
    />
    <PassengerFormBottomSheet
      v-if="activePassengerKey && activeSchema"
      :name="passengerFormBottomSheetName"
      :saved-passengers-bottom-sheet-name="savedPassengersBottomSheetName"
      :all-passengers="combinedPassengers"
      :passenger-idx="passengerIdxToEdit"
      :passenger-on-card="
        passengerOnCardForForm || formPassengerConfig.emptyPassenger
      "
      :passenger-key="activePassengerKey"
      :schema="activeSchema"
      :is-domestic="isDomestic"
      :provider-class="providerClass"
      :is-new-passenger="isNewPassenger"
      @onPassengerEdit="handlePassengerEdit"
      @onPassengerCreate="handlePassengerCreate"
    />
    <MissingPassengerDataModal
      :modal-name="missingPassengerDataModalName"
      @onAgree="handleMissingData"
    />
  </section>
</template>

<script setup lang="ts">
  import { computed, nextTick, onMounted, ref, watch } from 'vue';
  import { noop } from 'lodash-es';
  import { useI18n } from 'vue-i18n';
  import { useVuelidate } from '@vuelidate/core';
  import { v4 as uuid } from 'uuid';
  import { storeToRefs } from 'pinia';
  import type { Airflow } from '@aviata/platform-types';
  import type {
    PassengersData,
    PassengerKey,
    Passenger,
  } from '@aviata/booking-data';
  import { controllerModel, providerClassLib } from 'shared';
  import { PublicOfferBottomSheet, useFirebaseStore } from 'freedom-shared';

  import {
    savedPassengersConfig,
    SavedPassengersBottomSheet,
  } from '@/features/saved-passengers';

  import { publicOfferApi } from '@/entities/public-offer';
  import { accountModel } from '@/entities/account';
  import {
    formPassengerLib,
    formPassengerConfig,
    formPassengerModel,
  } from '@/entities/form-passenger';

  import { api } from '@/shared/api';
  import { sentryModel, useUserStore } from '@/shared/model';
  import { analyticsEventsLib } from '@/shared/lib';

  import type { DocScanData } from '../api/doc-scan';
  import { convertPassengerToPayload } from '../lib';
  import {
    PASSENGER_FORM_BOTTOM_SHEET,
    PASSENGER_ADD_METHOD_BOTTOM_SHEET,
    PASSENGER_DOCUMENT_PHOTO_BOTTOM_SHEET,
  } from '../config';
  import { usePassengersForm } from '../model';
  import type { PassengerWithDocType } from '../types';
  import MultiplePassengerBlock from './MultiplePassengerBlock.vue';
  import PassengerFormBottomSheet from './PassengerFormBottomSheet.vue';
  import PassengerAddMethodBottomSheet from './PassengerAddMethodBottomSheet.vue';
  import PassengerDocumentPhotoBottomSheet from './PassengerDocumentPhotoBottomSheet.vue';
  import MissingPassengerDataModal from './MissingPassengerDataModal.vue';

  function reformatApiDate(apiDate: string): string {
    const [year, month, day] = apiDate.split('-');
    return `${day}.${month}.${year}`;
  }

  function mapScanDataToPassenger(data: DocScanData): Passenger {
    return {
      firstName: data.first_name,
      lastName: data.last_name,
      gender: data.gender,
      dob: reformatApiDate(data.dob),
      citizenship: data.citizenship,
      iin: data.iin ?? '',
      airlineBonusCard: '',
      document: {
        number: data.document.number,
        expiresAt: data.document.expires_at
          ? reformatApiDate(data.document.expires_at)
          : '',
      },
    } as Passenger;
  }

  const props = defineProps<{
    isDomestic: boolean;
    passengers: PassengersData;
    passengerKeys: PassengerKey[];
    schema: Airflow.Offer.Schema.Passengers;
    providerClass?: string;
    departureDate?: string;
    /**
     * Skip the on-mount auto-open of the saved-passengers sheet for the
     * first empty passenger. QuickBookingPage hosts this widget purely for
     * its nested bottom-sheets and opens them on demand via `openSavedList`,
     * so the implicit "pop the sheet open right after the page paints"
     * behavior is unwanted there.
     */
    disableAutoOpen?: boolean;
  }>();

  const emit = defineEmits<{
    (
      e: 'onPassengerUpdate',
      updatedPassenger: { key: PassengerKey; passenger: Passenger },
    ): void;
  }>();

  const { t } = useI18n();
  const validations = useVuelidate();
  const bottomSheet = controllerModel.useBottomSheet();

  const { serializedRemoteConfig } = storeToRefs(useFirebaseStore());
  const isPhotoEnabled = computed(
    () =>
      serializedRemoteConfig.value
        ?.avfree_pax_data_auto_filling_by_photo_enabled === true,
  );
  const modal = controllerModel.useModal();

  const accountData = accountModel.useAccount();

  const {
    savedPassengers,
    localPassengers,
    combinedPassengers,
    getLocalPassengers,
    handleSavedPassengerUpdate,
    updateLocalPassenger,
    activePassengerKey,
    activeSelectedPassenger,
    passengerIdxToEdit,
    passengerOnCardForForm,
    isNewPassenger,
    activePassengerType,
    activeSchema,
    checkIfPassengerHasRequiredDocumentFields,
  } = usePassengersForm(props.schema);

  const createPassengerMutation = formPassengerModel.useCreatePassenger();
  const userStore = useUserStore();

  function saveLocalPassenger(passenger: PassengerWithDocType) {
    try {
      const stored = sessionStorage.getItem(
        savedPassengersConfig.SESSION_STORAGE_KEY,
      );
      const passengers: PassengerWithDocType[] = stored
        ? JSON.parse(stored)
        : [];
      const existingIdx = passengers.findIndex(
        pax =>
          pax.id === passenger.id ||
          pax.document.number === passenger.document.number,
      );
      if (existingIdx >= 0) {
        passengers[existingIdx] = passenger;
      } else {
        passengers.push(passenger);
      }
      sessionStorage.setItem(
        savedPassengersConfig.SESSION_STORAGE_KEY,
        JSON.stringify(passengers),
      );
    } catch (error) {
      sentryModel.captureException(
        sentryModel.getReadableError(
          'ERR_FAILED_TO_SAVE_LOCAL_PASSENGER',
          error,
        ),
      );
    }
  }

  // Bottom sheet state
  const savedPassengersBottomSheetName =
    savedPassengersConfig.SAVED_PASSENGERS_BOTTOM_SHEET;
  const passengerAddMethodBottomSheetName =
    PASSENGER_ADD_METHOD_BOTTOM_SHEET + uuid();
  const passengerDocumentPhotoBottomSheetName =
    PASSENGER_DOCUMENT_PHOTO_BOTTOM_SHEET + uuid();
  const passengerFormBottomSheetName = PASSENGER_FORM_BOTTOM_SHEET + uuid();
  const missingPassengerDataModalName = `missing-passenger-data-modal-${uuid()}`;
  const docScanPublicOfferBsName = `doc-scan-public-offer-${uuid()}`;
  const isBottomSheetTransitioning = ref(false);
  const isEditingActivePassenger = ref(false);

  const isSamePassenger = (left: Passenger | null, right: Passenger | null) => {
    if (!left || !right) return false;
    const leftId = (left as PassengerWithDocType).id;
    const rightId = (right as PassengerWithDocType).id;

    if (leftId && rightId) return leftId === rightId;

    return left.document.number === right.document.number;
  };

  const handleOpenSavedPassengersSheet = (
    key: PassengerKey,
    selectedPassenger: Passenger | null,
  ) => {
    analyticsEventsLib.savedPaxListOpen({
      key,
      providerClass: props.providerClass,
    });
    activePassengerKey.value = key;
    activeSelectedPassenger.value = selectedPassenger;
    localPassengers.value = getLocalPassengers();
    bottomSheet.show(savedPassengersBottomSheetName);
  };

  const handlePassengerFormOpen = (
    idx: number | null,
    prefilledPassenger?: Passenger,
  ) => {
    isNewPassenger.value = idx === null;
    passengerIdxToEdit.value = idx;
    isBottomSheetTransitioning.value = true;

    if (idx !== null && activePassengerKey.value) {
      const passengerToEdit = combinedPassengers.value[idx];
      const previousSelectedPassenger = activeSelectedPassenger.value;
      const currentPassenger = getTypedPassenger(activePassengerKey.value);
      passengerOnCardForForm.value = passengerToEdit;
      activeSelectedPassenger.value = passengerToEdit;
      isEditingActivePassenger.value =
        isSamePassenger(passengerToEdit, previousSelectedPassenger) ||
        isSamePassenger(passengerToEdit, currentPassenger);
    } else {
      passengerOnCardForForm.value =
        prefilledPassenger ?? formPassengerConfig.emptyPassenger;
      isEditingActivePassenger.value = true;
    }

    bottomSheet.hide(savedPassengersBottomSheetName);
    bottomSheet.hide(passengerAddMethodBottomSheetName);
    bottomSheet.hide(passengerDocumentPhotoBottomSheetName);
    bottomSheet.show(passengerFormBottomSheetName);
    nextTick(() => {
      isBottomSheetTransitioning.value = false;
    });
  };

  const handleAddNewPassengerRequest = () => {
    if (!isPhotoEnabled.value) {
      handlePassengerFormOpen(null);
      return;
    }
    isBottomSheetTransitioning.value = true;
    bottomSheet.hide(savedPassengersBottomSheetName);
    nextTick(() => {
      bottomSheet.show(passengerAddMethodBottomSheetName);
      nextTick(() => {
        isBottomSheetTransitioning.value = false;
      });
    });
  };

  const onAddMethodPhoto = () => {
    bottomSheet.hide(passengerAddMethodBottomSheetName);
    bottomSheet.show(passengerDocumentPhotoBottomSheetName);
  };

  const handlePassengerUpdate = (updated: Passenger) => {
    if (!activePassengerKey.value) return;

    // Проверяем обязательные поля документа только для FunSun
    // и только если пассажир выбран из списка сохраненных (имеет id)
    // Новые пассажиры с заполненными SM полями не должны вызывать модальное окно,
    // так как они могут быть сохранены позже в PassengerFormBottomSheet
    const isPassengerFromSavedList = !!(updated as PassengerWithDocType).id;
    const shouldCheckDocumentFields =
      providerClassLib.isFunSun(props.providerClass) &&
      isPassengerFromSavedList;

    if (
      shouldCheckDocumentFields &&
      !checkIfPassengerHasRequiredDocumentFields(
        updated as PassengerWithDocType,
      )
    ) {
      passengerOnCardForForm.value = updated;
      activeSelectedPassenger.value = updated;
      modal.show(missingPassengerDataModalName);
    } else {
      bottomSheet.hide(savedPassengersBottomSheetName);
      bottomSheet.hide(passengerAddMethodBottomSheetName);
      bottomSheet.hide(passengerDocumentPhotoBottomSheetName);
      activeSelectedPassenger.value = updated;
      emit('onPassengerUpdate', {
        key: activePassengerKey.value,
        passenger: updated,
      });
    }
  };

  const onAddMethodManual = () => {
    handlePassengerFormOpen(null);
  };

  const handleDocumentAutofillComplete = async (data?: DocScanData) => {
    if (data && activePassengerKey.value) {
      const passengerWithTempId: PassengerWithDocType = {
        ...mapScanDataToPassenger(data),
        id: uuid(),
      };

      saveLocalPassenger(passengerWithTempId);
      localPassengers.value = getLocalPassengers();
      handlePassengerUpdate(passengerWithTempId);

      const userId = await userStore.getUserId();
      if (userId) {
        createPassengerMutation
          .createPassengerWithCallback(
            {
              userId,
              passenger: convertPassengerToPayload({
                passenger: passengerWithTempId,
              }),
            },
            createdPassenger => {
              const passengerWithBackendId: PassengerWithDocType = {
                ...passengerWithTempId,
                id: createdPassenger.id,
              };
              saveLocalPassenger(passengerWithBackendId);
              localPassengers.value = getLocalPassengers();
            },
          )
          .catch(noop);
      }
    } else {
      handlePassengerFormOpen(null);
    }
  };

  const handleDocumentAutofillEdit = (data: DocScanData) => {
    handlePassengerFormOpen(null, mapScanDataToPassenger(data));
  };

  const handleMissingData = () => {
    const passengerId = (passengerOnCardForForm.value as PassengerWithDocType)
      .id;
    const idx = passengerId
      ? combinedPassengers.value.findIndex(pax => {
          const savedPax = pax as PassengerWithDocType;
          return savedPax.id === passengerId;
        })
      : -1;

    passengerIdxToEdit.value = idx >= 0 ? idx : null;
    bottomSheet.hide(passengerAddMethodBottomSheetName);
    bottomSheet.hide(passengerDocumentPhotoBottomSheetName);
    bottomSheet.show(passengerFormBottomSheetName);
  };

  const handlePassengerCreate = (newPax: Passenger) => {
    if (!activePassengerKey.value) return;

    // Если пассажир уже был создан с локальным id и теперь пришёл с бэковским id,
    // нужно обновить его в localPassengers и sessionStorage
    const passengerWithBackendId = newPax as PassengerWithDocType;
    if (passengerWithBackendId.id) {
      const localIdx = localPassengers.value.findIndex(
        pax =>
          (pax as PassengerWithDocType).id === passengerWithBackendId.id ||
          pax.document.number === passengerWithBackendId.document.number,
      );
      if (localIdx >= 0) {
        // Обновляем существующего пассажира с новым id
        localPassengers.value[localIdx] = passengerWithBackendId;
        sessionStorage.setItem(
          savedPassengersConfig.SESSION_STORAGE_KEY,
          JSON.stringify(localPassengers.value),
        );
      }
    }

    localPassengers.value = getLocalPassengers();
    passengerIdxToEdit.value = combinedPassengers.value.length - 1;
    passengerOnCardForForm.value = newPax;
    activeSelectedPassenger.value = newPax;
    // Новый пассажир всегда обновляется напрямую без проверки SM полей,
    // так как он был только что создан и может быть дополнен позже
    isNewPassenger.value = false;
    emit('onPassengerUpdate', {
      key: activePassengerKey.value,
      passenger: newPax,
    });
  };

  const handlePassengerEdit = (updated: Passenger, idx: number) => {
    if (!activePassengerKey.value) return;

    if (idx < savedPassengers.value.length) {
      handleSavedPassengerUpdate(idx, updated);
    } else {
      updateLocalPassenger(idx, updated);
    }

    const editedPassenger = passengerOnCardForForm.value;
    passengerOnCardForForm.value = updated;

    // Если редактированный пассажир является текущим пассажиром на карточке,
    // обновляем его напрямую (без проверки SM полей, так как он уже был сохранен)
    const currentPassenger = getTypedPassenger(activePassengerKey.value);
    const isSelectedPassenger = isSamePassenger(
      activeSelectedPassenger.value,
      editedPassenger,
    );
    const shouldUpdateActivePassenger =
      isEditingActivePassenger.value ||
      isSamePassenger(updated, currentPassenger) ||
      isSelectedPassenger;
    if (shouldUpdateActivePassenger) {
      activeSelectedPassenger.value = updated;
      isEditingActivePassenger.value = false;
      emit('onPassengerUpdate', {
        key: activePassengerKey.value,
        passenger: updated,
      });
    }

    passengerIdxToEdit.value = idx;
  };

  /**
   * Need to use type casting since we need to check if given key is present in passengers.
   * The given keys are always present according to the business logic, so we need to either add a
   * type guard that guarantees it or use type casting
   *
   * TODO: Add type guard that guarantees that Passenger is not undefined
   */
  function getTypedPassenger(key: PassengerKey) {
    return props.passengers[key] as Passenger;
  }

  const validPassengersAmount = computed(() => {
    const validAmount = props.passengerKeys
      .filter(passengerKey => props.passengers[passengerKey].firstName)
      .map(validations.value.$getResultsForChild)
      .filter(validation => (validation ? !validation.$invalid : false)).length;

    return t('passengersData.validPassengersAmount', {
      value: validAmount,
      total: props.passengerKeys.length,
    });
  });

  const handleAllBottomSheetsClose = () => {
    activePassengerKey.value = null;
  };

  watch(
    () => accountData.data.value?.passengers,
    passengers => {
      if (passengers && Array.isArray(passengers)) {
        savedPassengers.value = passengers.map(
          formPassengerLib.mapSavedPassenger,
        );
        localPassengers.value = getLocalPassengers();
      }
    },
    { immediate: true },
  );

  watch(
    () => savedPassengers.value,
    () => {
      localPassengers.value = getLocalPassengers();
    },
  );

  watch(bottomSheet.state, (_, prevState) => {
    const isAnySheetVisible =
      bottomSheet.isVisible(savedPassengersBottomSheetName) ||
      bottomSheet.isVisible(passengerFormBottomSheetName) ||
      bottomSheet.isVisible(passengerAddMethodBottomSheetName) ||
      bottomSheet.isVisible(passengerDocumentPhotoBottomSheetName);
    if (!isBottomSheetTransitioning.value && !isAnySheetVisible) {
      handleAllBottomSheetsClose();
    }
  });

  onMounted(() => {
    localPassengers.value = getLocalPassengers();
    if (props.disableAutoOpen) {
      return;
    }
    const firstKey = props.passengerKeys[0];
    if (firstKey && !getTypedPassenger(firstKey).firstName) {
      nextTick(() => {
        handleOpenSavedPassengersSheet(firstKey, getTypedPassenger(firstKey));
      });
    }
  });

  defineExpose({
    openSavedList(key: PassengerKey) {
      handleOpenSavedPassengersSheet(key, getTypedPassenger(key));
    },
  });
</script>
